import { Injectable } from '@angular/core';
import { dsDtos } from '@vectronic/openApi-admin';
import moment from 'moment/moment';
import { ParameterWithData } from '../models/device/parameterItem';

@Injectable({ providedIn: 'root' })
export class ParameterFormatService {
  /**
   * Parameters that should render as an empty string for specific data values.
   * Keyed by `${subsystemIdentifier}:${identifier}`.
   */
  private static readonly EMPTY_DATA_VALUES: Record<string, Set<string>> = {
    '10:22': new Set(['0']), // VIT Receiver - ID
    '10:17': new Set(['0']), // MIT Receiver - ID
    '14:2': new Set(['0', '1']), // Camera ID
  };

  private shouldShowEmpty(parameter: dsDtos.Parameter, data: string): boolean {
    if (parameter.subsystemIdentifier == null || parameter.identifier == null) return false;
    const values =
      ParameterFormatService.EMPTY_DATA_VALUES[
        `${parameter.subsystemIdentifier}:${parameter.identifier}`
      ];
    return !!values && values.has(data);
  }

  getReadOnlyFormat(
    { factory, user, parameter }: ParameterWithData,
    presetPatternParameter?: dsDtos.Parameter,
    showFactory = false,
    showOnlyFactoryLabel = false,
    subsystems?: dsDtos.Subsystem[],
  ) {
    const factoryValue = this.getFormattedData(
      factory,
      parameter,
      presetPatternParameter,
      subsystems,
    );
    const userValue = this.getFormattedData(user, parameter, presetPatternParameter, subsystems);
    if (userValue) {
      const factorySuffix =
        factoryValue && userValue !== factoryValue && showFactory
          ? `(Factory: ${factoryValue})`
          : '';
      return `${userValue} ${factorySuffix}`;
    } else return showOnlyFactoryLabel ? `${factoryValue} (Factory)` : factoryValue;
  }

  /**
   * Returns a formatted value based on the parameter data.
   * @param {ParameterData} parameterData - The parameter data.
   * @param {Parameter} parameter - The parameter object.
   * @param {Parameter} presetPatternParameter - The parameter object.
   * @param {Subsystem[]} subsystems - Optional list of subsystems for visibility flags formatting.
   * @returns {string} The formatted data.
   */
  getFormattedData(
    parameterData: dsDtos.ParameterData,
    parameter: dsDtos.Parameter,
    presetPatternParameter?: dsDtos.Parameter,
    subsystems?: dsDtos.Subsystem[],
  ): string {
    const data = parameterData?.data;
    if (!data) return '';
    if (this.shouldShowEmpty(parameter, data)) return '';
    const { value, enumCoding, unit, factor, name } = parameter;
    if (enumCoding && data in enumCoding) {
      return enumCoding[data];
    }
    switch (value) {
      case 'DATE_TIME':
        return this.getFormattedDateTime(data);
      case 'DAYTIME':
        return this.getFormattedTimeOfDay(parseInt(data));
      case 'DATE':
        return this.getFormattedDate(data);
      case 'DATE_AND_VERSION_1_1_1':
        return this.getFormattedDateAndVersion(data);
      case 'TIME_SPAN':
        return this.getFormattedTimeSpan(parseInt(data), factor);
      case 'TIME_ZONE':
        return ParameterFormatService.getFormattedTimeZone(parseInt(data));
      case 'UNSIGNED_INT':
        return this.getFormattedUnsignedInt(parseInt(data), unit);
      case 'VHF_PATTERN':
        return this.getVhfPattern(data, presetPatternParameter?.enumCoding);
      case 'ENUMERATION':
        return enumCoding?.[data] ?? data;
      case 'FLAGS':
        if (name === 'Visibility Flags' && subsystems?.length) {
          return this.getFormattedVisibilityFlags(data, subsystems);
        }
        return unit ? `${data} ${unit}` : data;
      case 'BAND_FLAGS':
        return this.getFormattedHexInt(parseInt(data), parameter.length);
      case 'SIGNED_INT':
      case 'STRING':
      case 'FLOAT':
      case 'TIME':
      case 'DATE_TIME_AND_UNSIGNED_INT':
      case 'VERSION_1_1_1_1':
      case 'VERSION_2_2':
      case 'VERSION_2_1_1':
      case 'HEX':
      case 'HEX_DOT':
      case 'CALIBRATION_BIAS':
      case 'CALIBRATION_MATRIX':
      case 'IP_ADDRESS':
      case 'LIST_UNSIGNED_INT_4':
      case 'LIST_UNSIGNED_INT_2':
      case 'LIST_DATE_TIME_AND_HEX_8':
      case 'RANGE':
      case 'MESSAGE_MODE':
      case 'GLOBALSTAR_ESN':
      case 'PHONE':
        return unit ? `${data} ${unit}` : data;
    }
  }

  private getFormattedHexInt(data: number, parameterByteLength: number): string {
    if (!data) return '';
    return '0x' + data.toString(16).padStart(parameterByteLength * 2, '0');
  }

  /**
   * Formats visibility flags to show disabled (invisible) subsystem names.
   * @param {string} data - The visibility flags value.
   * @param {Subsystem[]} subsystems - The list of subsystems.
   * @returns {string} Comma-separated list of disabled subsystem names, or '' if all are visible.
   */
  private getFormattedVisibilityFlags(data: string, subsystems: dsDtos.Subsystem[]): string {
    if (!data) return '';
    return (
      subsystems
        .filter((s) => this.isSubsystemVisible(s.identifier, data))
        .map((s) => s.name)
        .join(', ') || ''
    );
  }

  isSubsystemVisible(subsystemIdentifier: number, visibilityFlags: string) {
    const numberOfBits = 32;
    const binaryString = Number(visibilityFlags).toString(2).padStart(numberOfBits, '0');
    return binaryString[numberOfBits - 1 - subsystemIdentifier] === '1';
  }

  /**
   * Converts a numerical timezone offset into a formatted string representation.
   * @param {number} offset - The timezone offset in 15m.
   * @returns {string} - Formatted timezone string in the format '+HH:MM' or '-HH:MM'.
   */
  static getFormattedTimeZone(offset: number): string {
    if (offset === undefined || isNaN(offset)) return '';
    // Convert negative hours to positive equivalent for calculation
    const positiveOffset = Math.abs(offset);

    // Calculate hours and minutes
    const formattedHour = Math.floor(positiveOffset / 4);
    const minutes = (positiveOffset % 4) * 15;

    // Format hour and minutes
    return (
      (offset < 0 ? '-' : '') +
      formattedHour.toString().padStart(2, '0') +
      ':' +
      minutes.toString().padStart(2, '0')
    );
  }

  /**
   * Calculates and returns the formatted time span based on the provided value and factor.
   * If the factor is not provided or is falsy, returns the original value.
   * @param {number} value - The value representing the time span.
   * @param {number} factor - The factor to divide the value by for formatting.
   * @returns {string} The formatted time span value.
   */
  private getFormattedTimeSpan(value: number, factor: number): string {
    return this.convertMillisecondToFormattedTimeSpan(value, factor);
  }

  /**
   * Converts milliseconds to a formatted time string.
   * @param {number} milliseconds - The number of milliseconds to convert.
   * @param {number} factor - The factor in milliseconds for rounding.
   * @returns {string} The formatted time string.
   */
  private convertMillisecondToFormattedTimeSpan(milliseconds: number, factor: number): string {
    const msPerSecond = 1000;
    const msPerMinute = 60 * msPerSecond;
    const msPerHour = 60 * msPerMinute;
    const msPerDay = 24 * msPerHour;
    const msPerMonth = 30 * msPerDay;
    const msPerYear = 365 * msPerDay;

    const units = [
      { label: 'y', value: Math.floor(milliseconds / msPerYear), threshold: msPerYear },
      { label: 'mo', value: 0, threshold: msPerMonth },
      { label: 'd', value: 0, threshold: msPerDay },
      { label: 'h', value: 0, threshold: msPerHour },
      { label: 'm', value: 0, threshold: msPerMinute },
      { label: 's', value: 0, threshold: msPerSecond },
      { label: 'ms', value: 0, threshold: 1 },
    ];

    milliseconds %= msPerYear;
    units[1].value = Math.floor(milliseconds / msPerMonth);
    milliseconds %= msPerMonth;
    units[2].value = Math.floor(milliseconds / msPerDay);
    milliseconds %= msPerDay;
    units[3].value = Math.floor(milliseconds / msPerHour);
    milliseconds %= msPerHour;
    units[4].value = Math.floor(milliseconds / msPerMinute);
    milliseconds %= msPerMinute;
    units[5].value = Math.floor(milliseconds / msPerSecond);
    milliseconds %= msPerSecond;
    units[6].value = milliseconds;

    const filteredUnits = units.filter((u) => u.value > 0 && factor <= u.threshold).slice(0, 3); // Keep only the top 3 units
    return filteredUnits.map((u) => `${u.value} ${u.label}`).join(' ');
  }

  /**
   * Converts time in seconds to a string representation of time in the format HH:MM:SS.
   * @param {number} timeInSeconds - Time duration in seconds.
   * @returns {string} - Formatted time string in HH:MM:SS format.
   */
  private getFormattedTimeOfDay(timeInSeconds: number): string {
    const hours = Math.floor(timeInSeconds / 3600);
    const remainingSeconds = timeInSeconds % 3600;
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;

    const formattedHours = hours < 10 ? '0' + hours : hours.toString();
    const formattedMinutes = minutes < 10 ? '0' + minutes : minutes.toString();

    let result = formattedHours + ':' + formattedMinutes;

    if (seconds !== 0) {
      const formattedSeconds = seconds < 10 ? '0' + seconds : seconds.toString();
      result += ':' + formattedSeconds;
    }

    return result;
  }

  /**
   * Converts a numeric value representing seconds since the year 2000 into a formatted date and time string.
   * The value can also be a special value, marked by starting with the ! character. In this case, the method
   * will return the special value without any formatting
   * @param {string} data - The numeric value representing seconds since the year 2000.
   * @returns {string} The formatted date and time string in the format 'YYYY-MM-DD HH:mm:ss'.
   */
  getFormattedDateTime(data: string): string {
    if (data?.startsWith('!')) {
      return data;
    }
    return moment('2000-01-01')
      .utc(true)
      .add({
        second: parseInt(data),
      })
      .format('YYYY-MM-DD HH:mm:ss');
  }

  /**
   * Converts a numeric value representing days since the year 2000 into a formatted date and time string.
   * @param {string} data - The numeric value representing days since the year 2000.
   * @returns {string} A formatted date string in the format 'YYYY-MM-DD'.
   */
  private getFormattedDate(data: string): string {
    return moment('2000-01-01')
      .utc(true)
      .add({
        day: parseInt(data),
      })
      .format('YYYY-MM-DD');
  }

  /**
   * Converts a string containing date and version information into a formatted string.
   * @param {string} data - The input string containing date and version separated by a comma.
   * @returns {string} A formatted string containing the version and the date in the format 'YYYY-MM-DD'.
   */
  private getFormattedDateAndVersion(data: string): string {
    const [date, version] = data.split(',');
    const formattedDate = this.getFormattedDate(date);
    return `${version} (${formattedDate})`;
  }

  /**
   * Converts a given value to an unsigned integer based on the provided unit.
   * @param {number} data The value to be converted.
   * @param {string} unit The unit of the value ('ms', 'mV', 'byte', 'µAh', 'Hz').
   * @returns {string} The converted value along with its unit, formatted according to browser locale.
   */
  getFormattedUnsignedInt(data: number, unit: string): string {
    if (unit === 'ms' && data > 1000) {
      return `${(data / 1000).toLocaleString(undefined, { maximumFractionDigits: 0, useGrouping: false })} s`;
    }
    if (unit === 'mV' && data > 1000) {
      return `${(data / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false })} V`;
    }
    if ((unit === 'byte' || unit === 'bytes') && data > 1000000) {
      return `${(data / 1000000).toLocaleString(undefined, { maximumFractionDigits: 0, useGrouping: false })} MB`;
    }
    if ((unit === 'byte' || unit === 'bytes') && data > 1000) {
      return `${(data / 1000).toLocaleString(undefined, { maximumFractionDigits: 0, useGrouping: false })} kB`;
    }
    if (unit === 'µAh' && data > 1000) {
      return `${(data / 1000).toLocaleString(undefined, { maximumFractionDigits: 0, useGrouping: false })} mAh`;
    }
    if (unit === 'Hz' && data > 1000000) {
      //decimal to 3 digits
      return `${(data / 1000000).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3, useGrouping: false })} MHz`;
    }
    return `${data.toLocaleString(undefined, { useGrouping: false })} ${unit || ''}`;
  }

  private getVhfPattern(data: string, patterns: Record<string, string>): string {
    if (!data) return '';
    const values = data.split(',');
    const [pulseType, pulseLength, periodLength] = values;
    const pulsePattern = patterns?.[pulseType] || pulseType;
    return `Pulse Type: ${pulsePattern}\nPulse Length: ${pulseLength} ms\nPeriod Length: ${periodLength} ms`;
  }
}
